import { CreateOrganization, useOrganization, useOrganizationList } from '@clerk/clerk-react'
import { Fragment, type ReactNode } from 'react'
import { setActiveOrgId } from '../api/client'
import { createOrganizationAppearance } from '../theme/clerkAppearance'
import LoadingOverlay from './LoadingOverlay'
import WorkspaceOrganizationSwitcher from './WorkspaceOrganizationSwitcher'

/**
 * Investigations are scoped to the active Clerk organization in the API JWT.
 * Without an active org, the backend returns 401 missing_organization.
 *
 * Child state belongs to one workspace, so the subtree is keyed by org id: a
 * switch remounts it and every fetch-on-mount effect reruns against the new org.
 * Query caches are separated by org id inside the hooks themselves.
 */
export default function OrganizationGate({ children }: { children: ReactNode }) {
  const { organization, isLoaded: orgLoaded } = useOrganization()
  const { userMemberships, isLoaded: listLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  })

  setActiveOrgId(organization?.id ?? null)

  if (!orgLoaded || !listLoaded) {
    return <LoadingOverlay minHeight="240px" />
  }

  if (organization) {
    return <Fragment key={organization.id}>{children}</Fragment>
  }

  const hasMemberships = (userMemberships.data?.length ?? 0) > 0

  return (
    <div className="page" style={{ maxWidth: 520, margin: '48px auto' }}>
      <h1 className="text-display-md" style={{ margin: '0 0 8px' }}>
        Select a workspace
      </h1>
      <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: '14px' }}>
        Incident investigations are tied to your Clerk organization. Choose an existing workspace
        or create one to load your investigation history.
      </p>
      <div style={{ marginBottom: '16px' }}>
        <WorkspaceOrganizationSwitcher variant="standalone" />
      </div>
      {!hasMemberships && (
        <div style={{ marginTop: '24px' }}>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--text-muted)' }}>
            No organization yet? Create your first workspace:
          </p>
          <CreateOrganization
            afterCreateOrganizationUrl="/investigations"
            appearance={createOrganizationAppearance}
          />
        </div>
      )}
    </div>
  )
}
